import {useChangeFunctions, useEventsSearchInfinite} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {EventType, ShiftEventDto} from '@hconnect/common/types'
import {CardBox, CardLinkWrapper} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography} from '@mui/material'
import moment from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SystemChangeRequestTable, SystemChangeRequestsFilters} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useScrollEnd, useShiftEventsCountPerStatus, useSystemChangeRequestsFilters} from '../hooks'
import {mapToSystemChangeRequestTableItem} from '../mappers'
import {SystemChangeRequestTableItem} from '../types'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  isMobile?: boolean
  onSystemChangeRequestClick?: (eventId: string) => void
  systemChangeRequestsLink?: string
}

const SYSTEM_CHANGE_REQUEST: EventType = 'systemChangeRequest'

export const SystemChangeRequestContainer = ({
  plantId,
  cardBoxSx,
  isMobile,
  onSystemChangeRequestClick,
  systemChangeRequestsLink
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const startOfTheYear = moment.tz(timezone).startOf('year')
  const endOfToday = moment.tz(timezone).endOf('day')

  const {
    data: eventsCountMap,
    isLoading: isLoadingEventsCount,
    isError: isEventsCountError,
    isRefetching: isRefetchingEventsCount,
    refetch: refetchEventsCount
  } = useShiftEventsCountPerStatus({
    plantId,
    type: SYSTEM_CHANGE_REQUEST,
    from: startOfTheYear.toISOString(),
    to: endOfToday.toISOString()
  })

  const filters = useSystemChangeRequestsFilters(eventsCountMap)

  const {
    data: paginatedData,
    isInitialLoading,
    isError,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useEventsSearchInfinite({
    plantId,
    type: SYSTEM_CHANGE_REQUEST,
    from: startOfTheYear.toISOString(),
    to: endOfToday.toISOString(),
    statuses: filters.value
  })

  const {
    data: changeFunctions,
    isInitialLoading: isInitialLoadingChangeFunctions,
    isError: isChangeFunctionsError,
    refetch: refetchChangeFunctions,
    isRefetching: isRefetchingChangeFunctions
  } = useChangeFunctions({language: i18n.language})

  const systemChangeRequestsTableData: SystemChangeRequestTableItem[] = useMemo(() => {
    const dataFlattened = paginatedData?.pages.flatMap((page) => page) ?? []
    return dataFlattened.map((item: ShiftEventDto) =>
      mapToSystemChangeRequestTableItem(item, changeFunctions)
    )
  }, [paginatedData, changeFunctions])

  const scrollContainerRef = useScrollEnd<HTMLDivElement>({
    onScrollEnd: () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    offset: 50
  })

  const handleTableRowClick = (eventId: string) => {
    onSystemChangeRequestClick?.(eventId)
  }

  const cardContent = (
    <CardBox
      data-test-id="system-change-requests-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        ...cardBoxSx
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box flex={1}>
          <Typography variant="h3" mb={3} data-test-id="system-change-requests-link-to-cockpit">
            {t('systemChangeRequests.cardTitle')}
          </Typography>
        </Box>
        <Chip
          icon={<CalendarTodayOutlined />}
          label={`${t('systemChangeRequests.label.ytd')} ${startOfTheYear.year()}`}
          size="small"
        />
      </Box>
      <DataContentWrapper
        isNoData={false}
        showLoader={isInitialLoadingChangeFunctions || isLoadingEventsCount}
        isError={isError || isChangeFunctionsError || isEventsCountError}
        retryFunction={() => {
          if (isError) void refetch()
          if (isChangeFunctionsError) void refetchChangeFunctions()
          if (isEventsCountError) void refetchEventsCount()
        }}
        isRefetching={isRefetching || isRefetchingChangeFunctions || isRefetchingEventsCount}
        progressSx={{color: 'primary.main'}}
      >
        <CardContentWithFiltersLayout
          filters={
            <SystemChangeRequestsFilters
              value={filters.value}
              onChange={filters.onChange}
              options={filters.options}
              customAllLabel={filters.customAllLabel}
              isMobile={isMobile}
            />
          }
          content={
            <SystemChangeRequestTable
              data={systemChangeRequestsTableData}
              onRowClick={handleTableRowClick}
              showTableLoading={isInitialLoading}
              plantId={plantId}
            />
          }
          contentContainerRef={scrollContainerRef}
        />
      </DataContentWrapper>
    </CardBox>
  )

  return systemChangeRequestsLink ? (
    <CardLinkWrapper
      data-test-id="system-change-requests-card-link-wrapper"
      href={systemChangeRequestsLink}
    >
      {cardContent}
    </CardLinkWrapper>
  ) : (
    cardContent
  )
}

const CardContentWithFiltersLayout = ({
  filters,
  content,
  contentContainerRef
}: {
  filters: React.ReactNode
  content: React.ReactNode
  contentContainerRef?: React.RefObject<HTMLDivElement>
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        gap: 3
      }}
    >
      {filters}
      <Box ref={contentContainerRef} sx={{flex: 1, overflowY: 'auto'}}>
        {content}
      </Box>
    </Box>
  )
}
